import type { Player, PlayerStatus } from '@sea-wars/protocol';
import { type ReactNode, useEffect, useState } from 'react';
import { useChallenges } from '../api/challenges.js';
import { api } from '../api/client.js';

/** Odświeżanie statusów (online / w grze) — wolniej niż wyzwania, to tylko podpowiedź. */
const PLAYERS_POLL_MS = 15_000;
/** Opóźnienie wyszukiwania po ostatnim naciśnięciu klawisza. */
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_LABEL: Record<PlayerStatus, string> = {
  online: 'online',
  in_game: 'w grze',
  offline: 'offline',
};

interface PlayersCardProps {
  /** Nagłówek karty — w lobby powitanie gracza. */
  readonly title: string;
  /** Przyciski obok nagłówka (np. „Zagraj z botem”). */
  readonly actions?: ReactNode;
  /** Zmiana wartości wymusza ponowne pobranie (np. ulubiony przełączony w innej karcie lobby). */
  readonly refreshKey: number;
  readonly onFavoriteChange: (userId: string, isFavorite: boolean) => void;
}

/**
 * Wszyscy gracze, nie tylko ostatni przeciwnicy (`docs/08-decyzje.md` #23) — wyzwanie bez
 * kodu i linku, także kogoś, z kim nie było jeszcze żadnej partii. Online na górze.
 */
export function PlayersCard({ title, actions, refreshKey, onFavoriteChange }: PlayersCardProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [players, setPlayers] = useState<Player[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { outgoing, challenge, cancel } = useChallenges();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `refreshKey` ma tylko wywołać ponowne pobranie
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await api.players(debouncedQuery);
        if (!cancelled) setPlayers(response.items);
      } catch {
        // Pojedynczy nieudany odczyt pomijamy — kolejny cykl spróbuje ponownie.
      }
    }
    void load();
    const timer = window.setInterval(() => void load(), PLAYERS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [debouncedQuery, refreshKey]);

  async function toggleFavorite(player: Player) {
    setBusyId(player.userId);
    try {
      if (player.isFavorite) await api.removeFavorite(player.userId);
      else await api.addFavorite(player.userId);
      setPlayers(
        (prev) =>
          prev?.map((item) =>
            item.userId === player.userId ? { ...item, isFavorite: !item.isFavorite } : item,
          ) ?? null,
      );
      onFavoriteChange(player.userId, !player.isFavorite);
    } finally {
      setBusyId(null);
    }
  }

  async function sendChallenge(player: Player) {
    setBusyId(player.userId);
    try {
      await challenge(player);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card">
      <div className="lobby__head">
        <h2 className="hand">{title}</h2>
        {actions}
      </div>
      <p className="app__note">
        Wyzwij gracza z listy — dostanie powiadomienie i jednym kliknięciem wejdzie do partii.
      </p>
      <label className="field">
        <span>Szukaj po nicku</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          maxLength={20}
          placeholder="np. Kapitan"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>

      {players && players.length === 0 && (
        <p className="app__note">
          {debouncedQuery
            ? 'Nikt o takim nicku.'
            : 'Nie ma jeszcze innych graczy — zaproś znajomych linkiem z „Moje konto”.'}
        </p>
      )}

      {players && players.length > 0 && (
        <ul className="players">
          {players.map((player) => {
            const busy = busyId === player.userId;
            const pending = outgoing.find(
              (item) =>
                item.kind === 'challenge' &&
                item.to.userId === player.userId &&
                item.status === 'pending',
            );
            return (
              <li key={player.userId} className="players__row">
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={busy}
                  aria-label={player.isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
                  onClick={() => void toggleFavorite(player)}
                >
                  {player.isFavorite ? '★' : '☆'}
                </button>
                <span className="players__name">
                  <span
                    className={`players__dot players__dot--${player.status}`}
                    aria-hidden="true"
                  />
                  {player.nickname}
                  <span className="players__status">{STATUS_LABEL[player.status]}</span>
                </span>
                {pending ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void cancel(pending.id)}
                  >
                    Anuluj wyzwanie
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--small"
                    disabled={busy || player.status === 'in_game'}
                    onClick={() => void sendChallenge(player)}
                  >
                    Wyzwij
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
